// APD Copilot scope: what a conversation is about. Pure, no database.
//
// Read docs/apd-copilot/FEATURE_CHARTER.md before changing anything here.
//
// A thread is bound to ONE scope when it is created, and the server resolves
// that scope against the caller's own tenant. The model never names the RFQ or
// line it is working on: tools read the scope from the thread, so a model (or
// text inside an email) cannot redirect a conversation to another deal.

import { z } from 'zod';

export const SCOPE_TYPES = ['workspace', 'rfq', 'rfq_line', 'catalog_issue'] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

/** What the client asks for. `ref` is a public id (rfq_…, li_…) or a catalog
 *  issue key; never a UUID. The server re-resolves it inside the tenant. */
export const ScopeInput = z.discriminatedUnion('type', [
  z.object({ type: z.literal('workspace') }),
  z.object({ type: z.literal('rfq'), ref: z.string().regex(/^rfq_[0-9A-Za-z]{10,40}$/) }),
  z.object({ type: z.literal('rfq_line'), ref: z.string().regex(/^li_[0-9A-Za-z]{10,40}$/) }),
  z.object({ type: z.literal('catalog_issue'), ref: z.string().regex(/^(slug|pn):.{1,280}$/) }),
]);
export type ScopeInput = z.infer<typeof ScopeInput>;

/** A scope after the server resolved it. Labels are for display only. */
export interface ResolvedScope {
  type: ScopeType;
  /** Public reference, null for the workspace. */
  ref: string | null;
  /** e.g. "RFQ-20261008-021", "RFQ-20261008-021 · P/N 761574B", "APD workspace". */
  label: string;
  rfqPublicId: string | null;
  rfqRef: string | null;
  lineItemPublicId: string | null;
  /** The line's identifier exactly as the customer entered it. */
  partNumber: string | null;
}

export function scopeKey(s: { type: ScopeType; ref: string | null }): string {
  return s.type === 'workspace' ? 'workspace' : `${s.type}:${s.ref}`;
}

/** The chip text: "Ask about …". */
export function askAboutLabel(s: Pick<ResolvedScope, 'type' | 'label' | 'partNumber' | 'rfqRef'>): string {
  if (s.type === 'rfq_line' && s.partNumber) return `Ask about ${s.partNumber}`;
  if (s.type === 'rfq' && s.rfqRef) return `Ask about ${s.rfqRef}`;
  return `Ask about ${s.label}`;
}
